// src/components/settings/PetBreedsTab.tsx

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, Alert, Table, TableRow } from '@mui/material';
import {
  referenceDataAPI,
  PetType,
  PetSize,
  PetBreed,
} from '../../services/api';
import { useErrorHandler } from '../../errors';
import { useGlobalToast } from '../../App';
import {
  ConfirmDeleteDialog,
  RefCountChip,
  SystemChip,
} from './shared/SettingsElements';
import SettingsHeaderRow from './shared/SettingsHeaderRow';
import LoadingSpinner from '../common/LoadingSpinner';
import SettingsTableActions from './shared/SettingsTableActions';
import {
  StyledTableContainer,
  StyledTableHead,
  StyledTableHeaderCell,
  StyledTableBody,
  StyledTableRow,
  StyledTableCell,
  StyledTableChipCell,
  StyledTableChipCellContent,
  StyledTableActionsCell,
} from './shared/SettingsTableElements';
import PetBreedDialog from './shared/dialogs/PetBreedDialog';
import SimilarityDialog from '../forms/shared/SimilarityDialog';
import { checkReferenceDataSimilarity } from '../../utils/referenceDataSimilarityHelper';

interface BreedsTabProps {
  usage: Record<number, number>;
  onDataChanged: () => void;
}

const BreedsTab: React.FC<BreedsTabProps> = ({ usage, onDataChanged }) => {
  const [items, setItems] = useState<PetBreed[]>([]);
  const [petTypes, setPetTypes] = useState<PetType[]>([]);
  const [petSizes, setPetSizes] = useState<PetSize[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<PetBreed | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [searchText, setSearchText] = useState('');
  const [showSimilarityDialog, setShowSimilarityDialog] = useState(false);
  const [similarItem, setSimilarItem] = useState<PetBreed | null>(null);
  const [pendingSave, setPendingSave] = useState<{
    breed: string;
    petTypeId: number;
    petSizeFk: number | undefined;
    active?: boolean;
  } | null>(null);
  const { handleError } = useErrorHandler();
  const { showSuccess, showError } = useGlobalToast();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [breeds, types, sizes] = await Promise.all([
        referenceDataAPI.getPetBreeds(),
        referenceDataAPI.getPetTypes(),
        referenceDataAPI.getPetSizes(),
      ]);
      setItems(breeds);
      setPetTypes(types);
      setPetSizes(sizes);
    } catch (e) {
      handleError(e);
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = useMemo(() => {
    if (!searchText) return items;
    const lower = searchText.toLowerCase();
    return items.filter(
      (i) =>
        i.breed.toLowerCase().includes(lower) ||
        (i as any).petTypeNavigation?.type?.toLowerCase().includes(lower) ||
        i.petSize?.size?.toLowerCase().includes(lower)
    );
  }, [items, searchText]);

  const openDialog = (item?: PetBreed) => {
    setEditing(item ?? null);
    setDialogOpen(true);
  };

  const proceedWithSave = async (formData: {
    breed: string;
    petTypeId: number;
    petSizeFk: number | undefined;
    active?: boolean;
  }) => {
    try {
      if (editing) {
        await referenceDataAPI.updatePetBreed(editing.id, {
          breed: formData.breed,
          petTypeId: formData.petTypeId,
          petSizeFk: formData.petSizeFk ?? undefined,
          active: formData.active,
        });
        showSuccess();
      } else {
        await referenceDataAPI.createPetBreed({
          breed: formData.breed,
          petTypeId: formData.petTypeId,
          petSizeFk: formData.petSizeFk,
          active: formData.active,
        });
        showSuccess();
      }
      await fetchData();
      onDataChanged();
      setDialogOpen(false);
    } catch (e) {
      handleError(e);
    }
  };

  const save = async (formData: {
    breed: string;
    petTypeId: number;
    petSizeFk: number | undefined;
    active?: boolean;
  }) => {
    // Scoped to PetType only - Size is intentionally ignored, so "Poodle"
    // under Small and "Poodle" under Large for the same Type still collide.
    const scopedItems = items.filter((i) => i.petType === formData.petTypeId);
    const { isExactMatch, match } = checkReferenceDataSimilarity(
      formData.breed,
      scopedItems,
      (i) => i.breed,
      (i) => i.id,
      editing?.id
    );

    if (isExactMatch) {
      showError('A breed with this name already exists for this pet type');
      return;
    }

    if (match) {
      setSimilarItem(match);
      setPendingSave(formData);
      setShowSimilarityDialog(true);
      return;
    }

    await proceedWithSave(formData);
  };

  const handleUseExisting = () => {
    setShowSimilarityDialog(false);
    setSimilarItem(null);
    setPendingSave(null);
    setDialogOpen(false);
  };

  const handleCreateAnyway = async () => {
    setShowSimilarityDialog(false);
    setSimilarItem(null);
    if (pendingSave) {
      await proceedWithSave(pendingSave);
    }
    setPendingSave(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await referenceDataAPI.deletePetBreed(deleteTarget.id);
      showSuccess();
      await fetchData();
      onDataChanged();
    } catch (e) {
      handleError(e);
    } finally {
      setDeleteTarget(null);
    }
  };

  if (loading) return <LoadingSpinner message="Loading Breeds..." size={80} />;

  return (
    <Box>
      <SettingsHeaderRow
        title="Breeds"
        onAdd={() => openDialog()}
        addLabel="Add Breed"
        buttonMinWidth="230px"
        onSearch={setSearchText}
        onClearSearch={() => setSearchText('')}
        searchText={searchText}
        searchPlaceholder="Search breeds..."
      />
      {items.length === 0 ? (
        <Alert severity="info">No breeds found.</Alert>
      ) : filtered.length === 0 ? (
        <Alert severity="info">No breeds match "{searchText}".</Alert>
      ) : (
        <StyledTableContainer>
          <Table size="small">
            <StyledTableHead>
              <TableRow>
                <StyledTableHeaderCell>Breed</StyledTableHeaderCell>
                <StyledTableHeaderCell>Type</StyledTableHeaderCell>
                <StyledTableHeaderCell>Size</StyledTableHeaderCell>
                <StyledTableHeaderCell align="center">
                  Refs
                </StyledTableHeaderCell>
                <StyledTableHeaderCell align="right">
                  Actions
                </StyledTableHeaderCell>
              </TableRow>
            </StyledTableHead>
            <StyledTableBody>
              {filtered.map((item, index) => {
                const refCount = usage[item.id] ?? 0;
                return (
                  <StyledTableRow key={item.id} isOdd={index % 2 === 0}>
                    <StyledTableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography>{item.breed}</Typography>
                        {item.isSystem && <SystemChip />}
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography>
                        {(item as any).petTypeNavigation?.type ?? '-'}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography>{item.petSize?.size ?? '-'}</Typography>
                    </StyledTableCell>
                    <StyledTableChipCell>
                      <StyledTableChipCellContent>
                        <RefCountChip count={refCount} />
                      </StyledTableChipCellContent>
                    </StyledTableChipCell>
                    <StyledTableActionsCell align="right">
                      <SettingsTableActions
                        itemId={item.id}
                        itemName={item.breed}
                        refCount={refCount}
                        isSystem={item.isSystem}
                        onEdit={() => openDialog(item)}
                        onDelete={() =>
                          setDeleteTarget({ id: item.id, name: item.breed })
                        }
                        refLabel="pet"
                      />
                    </StyledTableActionsCell>
                  </StyledTableRow>
                );
              })}
            </StyledTableBody>
          </Table>
        </StyledTableContainer>
      )}

      <PetBreedDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={save}
        editing={editing}
        petTypes={petTypes}
        petSizes={petSizes}
      />

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        title="Delete breed?"
        message={`Are you sure you want to delete "${deleteTarget?.name}"?`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <SimilarityDialog
        open={showSimilarityDialog}
        entityType="petBreed"
        similarEntity={similarItem}
        isEditMode={!!editing}
        onUseExisting={handleUseExisting}
        onCreateNew={handleCreateAnyway}
        onClose={() => setShowSimilarityDialog(false)}
      />
    </Box>
  );
};

export default BreedsTab;
